import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  getDocumentDetail,
  DocumentDetail as DocumentDetailType,
  ingestDocument,
  updateDocumentStatus,
  deleteDocument,
  updateSourcePage,
} from '../api/documents';
import PageContainer from '../components/PageContainer';

type Tab = 'rendered' | 'raw' | 'chunks' | 'source';

const DocumentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [doc, setDoc] = useState<DocumentDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('rendered');
  const [chunkQuery, setChunkQuery] = useState('');
  const renderedRef = useRef<HTMLDivElement | null>(null);
  const [sourceDraft, setSourceDraft] = useState('');
  const [savingSource, setSavingSource] = useState(false);

  useEffect(() => {
    if (id) loadDocument(parseInt(id));
  }, [id]);

  const loadDocument = async (docId: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getDocumentDetail(docId);
      setDoc(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  const handleIngest = async () => {
    if (!doc || !window.confirm('Publish this document so the chatbot can use it?')) return;
    try {
      await ingestDocument(doc.id);
      loadDocument(doc.id);
    } catch (err: any) {
      alert('Failed to publish: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!doc) return;
    try {
      await updateDocumentStatus(doc.id, newStatus);
      loadDocument(doc.id);
    } catch {
      alert('Failed to update status');
    }
  };

  const handleDelete = async () => {
    if (!doc) return;
    if (!window.confirm(`Delete "${doc.title}"? This permanently removes the document, all its chunks, and version history.`)) return;
    try {
      await deleteDocument(doc.id);
      navigate('/documents');
    } catch (err: any) {
      alert('Failed to delete: ' + (err.response?.data?.detail || err.message));
    }
  };

  const filteredChunks = useMemo(() => {
    if (!doc) return [];
    const q = chunkQuery.trim().toLowerCase();
    if (!q) return doc.chunks;
    return doc.chunks.filter(c => c.text.toLowerCase().includes(q));
  }, [doc, chunkQuery]);

  const stats = useMemo(() => {
    if (!doc) return null;
    const total = doc.chunks.length;
    const embedded = doc.chunks.filter(c => c.has_embedding).length;
    const withOffsets = doc.chunks.filter(c => c.start_offset != null && c.end_offset != null).length;
    const chars = doc.raw_text?.length ?? 0;
    const avgChunkChars = total > 0 ? Math.round(doc.chunks.reduce((s, c) => s + c.text.length, 0) / total) : 0;
    return { total, embedded, withOffsets, chars, avgChunkChars };
  }, [doc]);

  if (loading) {
    return (
      <PageContainer>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-primary-200 rounded w-64" />
          <div className="h-64 bg-primary-200 rounded" />
        </div>
      </PageContainer>
    );
  }

  if (error || !doc) {
    return (
      <PageContainer>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
          {error || 'Document not found'}
        </div>
        <Link to="/documents" className="text-sm text-primary-700 mt-4 inline-block hover:underline">&larr; Back to Documents</Link>
      </PageContainer>
    );
  }

  const dir = doc.language === 'ar' ? 'rtl' : 'ltr';
  const statusBadge = (s: string) => {
    if (s === 'active') return <span className="badge-success">Active</span>;
    if (s === 'inactive') return <span className="badge-neutral">Inactive</span>;
    if (s === 'ingestion_failed') return <span className="badge-error">Failed</span>;
    return <span className="badge-warning">Draft</span>;
  };

  return (
    <PageContainer>
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <Link to="/documents" className="text-xs text-primary-400 hover:text-primary-600 transition-colors mb-2 inline-flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
            Back to Documents
          </Link>
          <h1 className="text-3xl font-semibold text-primary-900 tracking-tight leading-tight">{doc.title}</h1>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {statusBadge(doc.status)}
            {doc.domain_name && (
              <span className="text-xs px-2 py-0.5 bg-primary-50 text-primary-700 rounded-full font-medium">
                {doc.domain_name}
              </span>
            )}
            <span className="text-xs px-2 py-0.5 bg-primary-100 text-primary-600 rounded-full uppercase font-mono">
              {doc.language}
            </span>
            <span className="text-xs text-primary-400">v{doc.version}</span>
            <span className="text-xs text-primary-400">
              {new Date(doc.created_at).toLocaleDateString()}
            </span>
            {doc.source_url && (
              <a href={doc.source_url} target="_blank" rel="noopener noreferrer"
                 className="text-xs text-primary-700 hover:underline inline-flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V13.5M19.5 4.5v4.5h-4.5M10 14L19.5 4.5"/>
                </svg>
                Source
              </a>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {doc.status === 'active' && (
            <a href={`/doc/${doc.id}`} target="_blank" rel="noopener noreferrer"
               className="btn-secondary text-xs px-3 py-2 inline-flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"/>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
              Public view
            </a>
          )}
          {(doc.status === 'draft' || doc.status === 'ingestion_failed') && (
            <button onClick={handleIngest} className="btn-primary text-xs px-4 py-2">Publish</button>
          )}
          {doc.status === 'active' && (
            <button onClick={() => handleStatusChange('inactive')} className="btn-secondary text-xs px-3 py-2">Deactivate</button>
          )}
          {doc.status === 'inactive' && (
            <button onClick={() => handleStatusChange('active')} className="btn-primary text-xs px-3 py-2">Activate</button>
          )}
          <button onClick={handleDelete} className="text-xs px-3 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors font-medium">
            Delete
          </button>
        </div>
      </div>

      {/* ── Stats strip ───────────────────────────────────────────── */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard label="Characters" value={stats.chars.toLocaleString()} />
          <StatCard label="Chunks" value={stats.total.toString()} />
          <StatCard
            label="Embedded"
            value={`${stats.embedded}/${stats.total}`}
            ok={stats.total > 0 && stats.embedded === stats.total}
          />
          <StatCard label="Avg chunk size" value={`${stats.avgChunkChars} chars`} />
        </div>
      )}

      {/* ── Layout: Content + Outline ─────────────────────────────── */}
      <div className="grid lg:grid-cols-[1fr_240px] gap-6">
        <div className="min-w-0">
          {/* tabs */}
          <div className="flex gap-1 bg-primary-100 p-1 rounded-xl w-fit mb-4">
            <TabBtn active={activeTab === 'rendered'} onClick={() => setActiveTab('rendered')}>Rendered</TabBtn>
            <TabBtn active={activeTab === 'raw'} onClick={() => setActiveTab('raw')}>Raw</TabBtn>
            <TabBtn active={activeTab === 'chunks'} onClick={() => setActiveTab('chunks')}>
              Chunks ({doc.chunks.length})
            </TabBtn>
            <TabBtn active={activeTab === 'source'} onClick={() => { setSourceDraft(doc.raw_text || ''); setActiveTab('source'); }}>
              Edit source page
            </TabBtn>
          </div>

          {/* rendered markdown */}
          {activeTab === 'rendered' && (
            <div className="bg-white rounded-xl border border-primary-200 shadow-sm">
              {doc.raw_text ? (
                <div ref={renderedRef} className="p-6 max-h-[78vh] overflow-y-auto" dir={dir}>
                  <div className="doc-prose">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc.raw_text}</ReactMarkdown>
                  </div>
                </div>
              ) : (
                <EmptyContent />
              )}
            </div>
          )}

          {/* raw text */}
          {activeTab === 'raw' && (
            <div className="bg-white rounded-xl border border-primary-200 shadow-sm">
              {doc.raw_text ? (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-primary-400">Version {doc.version}</span>
                    <span className="text-xs text-primary-400">{doc.raw_text.length.toLocaleString()} characters</span>
                  </div>
                  <pre
                    className="text-sm text-primary-700 whitespace-pre-wrap font-mono leading-relaxed bg-primary-50 rounded-lg p-5 border border-primary-100 max-h-[72vh] overflow-y-auto"
                    dir={dir}
                  >
                    {doc.raw_text}
                  </pre>
                </div>
              ) : (
                <EmptyContent />
              )}
            </div>
          )}

          {/* source page editor */}
          {activeTab === 'source' && (
            <div className="bg-white rounded-xl border border-primary-200 shadow-sm p-6 space-y-4">
              <div className="rounded-xl bg-primary-50 border border-primary-200 px-4 py-3 text-sm text-primary-600 leading-relaxed">
                This is the <span className="font-semibold text-primary-700">source page</span> — the full original
                document that opens when someone taps a citation in the chat. Paste the complete official text here
                (markdown works). It's display-only: editing it never changes what the chatbot retrieves, so it's
                always safe to fix.
              </div>
              <textarea
                value={sourceDraft}
                onChange={(e) => setSourceDraft(e.target.value)}
                rows={20}
                dir="auto"
                className="input-base font-mono text-sm leading-relaxed"
                placeholder="Paste the full original document text (markdown supported)…"
              />
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={async () => {
                    if (!doc || sourceDraft.trim().length < 10) return;
                    setSavingSource(true);
                    try {
                      const r = await updateSourcePage(doc.id, sourceDraft);
                      alert(`Source page saved as version ${r.version}.`);
                      loadDocument(doc.id);
                      setActiveTab('rendered');
                    } catch (err: any) {
                      alert('Save failed: ' + (err.response?.data?.detail || err.message));
                    } finally { setSavingSource(false); }
                  }}
                  disabled={savingSource || sourceDraft.trim().length < 10}
                  className="btn-primary"
                >
                  {savingSource ? 'Saving…' : 'Save as new version'}
                </button>
                <a href={`/doc/${doc.id}`} target="_blank" rel="noopener noreferrer" className="btn-secondary text-xs gap-1.5">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                  </svg>
                  View public page
                </a>
                <span className="text-xs text-primary-400">
                  {sourceDraft.length.toLocaleString()} characters · currently v{doc.version}
                </span>
              </div>
            </div>
          )}

          {/* chunks */}
          {activeTab === 'chunks' && (
            <div className="bg-white rounded-xl border border-primary-200 shadow-sm p-5">
              {doc.chunks.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-sm text-primary-400 mb-2">Not published yet</p>
                  <p className="text-xs text-primary-400">Publish the document so the chatbot can use it.</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <input
                      type="search"
                      value={chunkQuery}
                      onChange={(e) => setChunkQuery(e.target.value)}
                      placeholder="Filter chunks…"
                      className="flex-1 max-w-md px-3 py-1.5 text-sm border border-primary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    />
                    <span className="text-xs text-primary-500 whitespace-nowrap">
                      {filteredChunks.length} of {doc.chunks.length}
                    </span>
                  </div>
                  <div className="space-y-3">
                    {filteredChunks.map(chunk => (
                      <ChunkCard key={chunk.id} chunk={chunk} dir={dir} />
                    ))}
                    {filteredChunks.length === 0 && (
                      <p className="text-sm text-primary-400 text-center py-8">No chunks match "{chunkQuery}".</p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* ── Side metadata panel ────────────────────────────────── */}
        <aside className="hidden lg:block">
          <div className="bg-white rounded-xl border border-primary-200 shadow-sm p-4 sticky top-6 space-y-3 text-sm">
            <h3 className="text-xs font-semibold text-primary-500 uppercase tracking-wider">Metadata</h3>
            <MetaRow label="Document ID" value={`#${doc.id}`} mono />
            <MetaRow label="Status" value={doc.status} />
            <MetaRow label="Language" value={doc.language} mono />
            <MetaRow label="Domain" value={doc.domain_name || 'General (no domain)'} />
            <MetaRow label="Version" value={`v${doc.version}`} />
            <MetaRow label="Created" value={new Date(doc.created_at).toLocaleString()} />
            {doc.effective_date && (
              <MetaRow label="Effective" value={new Date(doc.effective_date).toLocaleDateString()} />
            )}
          </div>
        </aside>
      </div>
    </PageContainer>
  );
};

const TabBtn: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
      active ? 'bg-white text-primary-900 shadow-sm' : 'text-primary-500 hover:text-primary-700'
    }`}
  >
    {children}
  </button>
);

const StatCard: React.FC<{ label: string; value: string; ok?: boolean }> = ({ label, value, ok }) => (
  <div className="bg-white rounded-xl border border-primary-200 px-4 py-3 shadow-sm">
    <div className="text-xs uppercase tracking-wider text-primary-400 font-semibold">{label}</div>
    <div className={`text-lg font-semibold mt-0.5 ${ok ? 'text-emerald-600' : 'text-primary-900'}`}>{value}</div>
  </div>
);

const MetaRow: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <div className="flex items-baseline justify-between gap-3">
    <span className="text-xs text-primary-500 uppercase tracking-wider">{label}</span>
    <span className={`text-xs text-primary-700 text-right ${mono ? 'font-mono' : ''}`}>{value}</span>
  </div>
);

const ChunkCard: React.FC<{ chunk: DocumentDetailType['chunks'][number]; dir: string }> = ({ chunk, dir }) => {
  const [expanded, setExpanded] = useState(false);
  const long = chunk.text.length > 320;
  const display = expanded || !long ? chunk.text : chunk.text.slice(0, 320) + '…';
  return (
    <div className="border border-primary-200 rounded-lg p-4 bg-primary-50/30 hover:bg-primary-50 transition-colors">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-mono font-semibold text-primary-500 bg-white px-1.5 py-0.5 rounded border border-primary-200">
            #{chunk.index} · id={chunk.id}
          </span>
          {chunk.has_embedding ? (
            <span className="text-xs px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded font-medium" title="bge-m3 embedding present">
              embedded
            </span>
          ) : (
            <span className="text-xs px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded font-medium">
              no embedding
            </span>
          )}
          {chunk.context_header && (
            <span className="text-xs px-1.5 py-0.5 bg-primary-50 text-primary-700 rounded font-medium" title={chunk.context_header}>context</span>
          )}
        </div>
        <div className="text-xs text-primary-400 font-mono">
          {chunk.start_offset != null && chunk.end_offset != null
            ? `offset ${chunk.start_offset.toLocaleString()}–${chunk.end_offset.toLocaleString()}`
            : 'no offsets'}
          {' · '}
          {chunk.text.length} chars
        </div>
      </div>
      <p className="text-sm text-primary-700 leading-relaxed whitespace-pre-wrap" dir={dir}>{display}</p>
      {long && (
        <button onClick={() => setExpanded(!expanded)} className="text-xs text-primary-700 hover:underline mt-2">
          {expanded ? 'Show less' : 'Show full chunk'}
        </button>
      )}
    </div>
  );
};

const EmptyContent: React.FC = () => (
  <div className="p-12 text-center text-sm text-primary-400">
    No content available. The document may not have been created with raw text.
  </div>
);

export default DocumentDetail;
